import { CompanyOfferSelectionComponent } from '../company-offers/company-offer-selection.component';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {CompanyInfo, TeamCompaniesService} from '../../services/teamcompanies/teamcompanies.service';
import {UiAlertService} from '../../services/ui-alert/ui-alert.service';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TeamsService} from '../../services/teams/teams.service';
import {EditCompanyDialogComponent, EditCompanyDialogModel} from '../modal/editcompanydialog/editcompanydialog.component';
import {SnackBarComponent} from '../snackbar/snackbar.component';
import {SetTeamCompanyComponentModel, SetTeamCompanyDialogComponent} from '../modal/setteamcompanydialog/setteamcompanydialog.component';
import {
  EditCompanyManagersDialogComponent,
  EditCompanyManagersDialogModel
} from '../modal/editcompanymanagersdialog/editcompanymanagersdialog.component';
import {NestedTreeControl} from '@angular/cdk/tree';
import {MatTreeNestedDataSource} from '@angular/material/tree';
import {MatTableDataSource} from '@angular/material/table';
import {ExporttocsvService} from '../../services/exporttocsv/exporttocsv.service';
import {Router} from '@angular/router';
import {UsersService} from '../../services/users/users.service';
import {TeamCompanyUsersService} from '../../services/teamcompanyusers/teamcompanyusers.service';
import {SessionService} from '../../services/session/session.service';
import {Partner, PartnersService} from '../../services/partners/partners.service';
import {
  CompanyProductsBrandOption,
  CompanyProductsCompanyOption,
  CompanyProductSettingMode,
  CompanyProductsDialogComponent,
  CompanyProductsDialogModel
} from '../modal/companyproductsdialog/companyproductsdialog.component';
import {
  CompanyProductTilesCategoriesDialogResult,
  CompanyProductTilesCategoriesDialogComponent,
  CompanyProductTilesCategoriesDialogModel
} from '../modal/company-product-tiles-categories-dialog/company-product-tiles-categories-dialog.component';
import {
  CompanyProductPayrollBenefitCategoriesDialogResult,
  CompanyProductPayrollBenefitCategoriesDialogComponent,
  CompanyProductPayrollBenefitCategoriesDialogModel
} from '../modal/company-product-payroll-benefit-categories-dialog/company-product-payroll-benefit-categories-dialog.component';
import {
  CompanyProductPlanLevelsDialogComponent,
  CompanyProductPlanLevelsDialogModel,
  CompanyProductPlanLevelsDialogResult,
} from '../modal/company-product-plan-levels-dialog/company-product-plan-levels-dialog.component';
import { PayrollBenefitsAssignmentService } from '../../services/payroll-benefits-assignment/payroll-benefits-assignment.service';
import {
  normalizeProductPayrollBenefitMap,
  normalizePayrollBenefitSubcategoryDefinitionsByCategory,
  PayrollBenefitSubcategoryDefinitionsByCategory,
  ProductBenefitMapping,
} from '../../services/payroll-benefits-assignment/payroll-benefit-categories';
import {
  GroupProductOnboardingStatusDialogComponent,
  GroupProductOnboardingStatusDialogData,
} from './group-product-onboarding-status-dialog/group-product-onboarding-status-dialog.component';
import {
  CompanySnackRewardsDialogComponent,
  CompanySnackRewardsCompanyOption,
  CompanySnackRewardsDialogModel,
  CompanySnackRewardsDialogResult,
} from '../modal/company-snack-rewards-dialog/company-snack-rewards-dialog.component';
import { RewardsAdminService } from '../../services/rewards-admin/rewards-admin.service';
import { ManagedFeatureFlagsService } from '../../services/managed-feature-flags/managed-feature-flags.service';

const COMPANY_SNACK_REWARD_SELECTION_FLAG = 'PAYROLL_ADMIN_COMPANY_SNACK_REWARD_SELECTION';

interface Node {
  name: string;
  type: string;
  id: string;
  payrollConfig?: any;
  metadata?: any;
  partnerDisplays?: PartnerTreeDisplay[];
  roleAssignmentCount?: number;
  roleAssignmentTooltip?: string;
  children: Array<Node>;
}

interface PartnerTreeDisplay {
  id: string;
  name: string;
  iconUrl?: string;
}

interface TeamAgentTeam {
  id: string;
  name: string;
  ownerName?: string;
  ownerUserId?: string;
  createdAt?: string;
  approved?: boolean | number;
}

interface TreeMoveAction {
  id: string;
  name: string;
  type: string;
  previousParentId: string;
  nextParentId: string;
}

@Component({
    selector: 'app-products-companies',
    templateUrl: './products-companies.component.html',
    styleUrls: ['./products-companies.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProductsCompaniesComponent implements OnInit {
  public readonly payrollTeamPlusDefaultHelpText =
    'Payroll Admin setting: new Payroll Admin-created teams for this employer, including UKG-created teams, default to the Plus plan. Existing teams keep their current plan.';
  public readonly includedPlusPlanHelpText =
    'When enabled, Plus Monthly plan charges are included for teams under this employer, so allowance funding does not add a separate Plus subscription fee.';

  treeControl = new NestedTreeControl<any>(node => node.children);
  dataSource = new MatTreeNestedDataSource<Node>();
  private companies: Array<CompanyInfo> = [];
  private partners: Partner[] = [];
  private partnersLoadPromise?: Promise<Partner[]>;
  private partnerLookup = new Map<string, PartnerTreeDisplay>();
  private roleAssignmentDetails = new Map<string, string[]>();
  private initializedForCurrentUser = false;
  public filter = '';
  public isSuperAdmin = false;
  public isCompanySnackRewardSelectionEnabled = false;
  public isTeamAgentOnly = false;
  public teamAgentCompanies = new MatTableDataSource<CompanyInfo>();
  public teamAgentTeams = new MatTableDataSource<TeamAgentTeam>();
  public selectedTeamAgentCompany?: CompanyInfo;
  public teamAgentCompaniesLoading = false;
  public teamAgentTeamsLoading = false;
  public fullCompanyTreeLoading = false;
  public fullCompanyTreeError = '';
  public isTreeMoveBusy = false;
  public readonly teamAgentCompanyColumns = ['name', 'domainName', 'actions'];
  public readonly teamAgentTeamColumns = ['name', 'ownerName', 'createdAt', 'approved', 'actions'];
  private lastTreeMoveSeries: TreeMoveAction[] = [];

  public get showTeamAgentCompanyPicker(): boolean {
    return this.teamAgentCompanies.data.length !== 1 || !this.selectedTeamAgentCompany;
  }

  public typeIcons = {
    wallit: {name: 'wallit', tooltip: 'Wallit master'},
    carrier: {name: 'business', tooltip: 'Carrier'},
    broker: {name: 'store', tooltip: 'Broker'},
    agency: {name: 'business_center', tooltip: 'Agency'},
    employer: {name: 'badge', tooltip: 'Employer/Client'},
    team: {name: 'receipt_long', tooltip: 'Team'}
  };

  constructor(
      private teamCompaniesService: TeamCompaniesService,
      private uiAlertService: UiAlertService,
      private dialog: MatDialog,
      private snackbar: MatSnackBar,
      private teamsService: TeamsService,
      private exportToCsvService: ExporttocsvService,
      private usersService: UsersService,
      private teamCompanyUsersService: TeamCompanyUsersService,
      private sessionService: SessionService,
      private partnersService: PartnersService,
      private payrollBenefitsAssignmentService: PayrollBenefitsAssignmentService,
      private rewardsAdminService: RewardsAdminService,
      private managedFeatureFlagsService: ManagedFeatureFlagsService,
      private router: Router
  ) {
  }

  ngOnInit(): void {
    this.usersService.meSubscribe((me: any) => {
      if (me) {
        this.isSuperAdmin = this.usersService.isAdmin();
        void this.loadCompanySnackRewardSelectionFeatureFlag();
        this.initializeItemsForCurrentUser();
      }
    });
  }

  private initializeItemsForCurrentUser(): void {
    if (this.initializedForCurrentUser) {
      return;
    }

    this.initializedForCurrentUser = true;
    if (this.isSuperAdmin) {
      this.loadFullCompanyTree();
      return;
    }

    this.teamCompanyUsersService.getManagers().then(result => {
      const roles = result?.plain?.() || result || [];
      this.isTeamAgentOnly = Array.isArray(roles)
        && roles.length > 0
        && roles.every((role: any) => role?.role === 'teamagent');

      if (this.isTeamAgentOnly) {
        this.loadTeamAgentCompanies();
      } else {
        this.loadFullCompanyTree();
      }
    }).catch(() => {
      this.snackbar.openFromComponent(SnackBarComponent, {data: 'Unable to load company permissions'});
    });
  }

  private loadFullCompanyTree(): void {
    this.loadPartners();
    this.loadItems();
  }

  getIcon(node: Node): string {
    // @ts-ignore
    return this.typeIcons[node.type]?.name;
  }

  getTooltip(node: Node): string {
    // @ts-ignore
    return this.typeIcons[node.type]?.tooltip;
  }

  private loadItems(): void {
    this.fullCompanyTreeLoading = true;
    this.fullCompanyTreeError = '';
    Promise.all([
      this.teamCompaniesService.getCompanies(),
      this.teamCompanyUsersService.getAllCompanyUsers(),
    ]).then(([companies, companyUsers]) => {
      const nextCompanies = companies?.plain?.() || companies || [];
      const nextCompanyUsers = companyUsers?.plain?.() || companyUsers || [];
      this.companies = nextCompanies;
      this.roleAssignmentDetails = this.buildRoleAssignmentDetails(nextCompanyUsers);
      this.dataSource.data = this.transformToNested(nextCompanies);
      if (this.dataSource.data.length > 0) {
        this.treeControl.expand(this.dataSource.data[0]);
      }
    }).catch(() => {
      this.fullCompanyTreeError = 'Unable to load companies.';
      this.snackbar.openFromComponent(SnackBarComponent, {data: 'Unable to load companies'});
    }).finally(() => {
      this.fullCompanyTreeLoading = false;
    });
  }

  private loadTeamAgentCompanies(): void {
    this.teamAgentCompaniesLoading = true;
    this.teamCompaniesService.getCompanies('teamagent').then(companies => {
      const nextCompanies = companies?.plain?.() || companies || [];
      this.teamAgentCompanies.data = Array.isArray(nextCompanies)
        ? nextCompanies
          .filter((item: any) => item?.type !== 'team')
          .sort((left: any, right: any) => `${left.name || ''}`.localeCompare(`${right.name || ''}`))
        : [];

      if (this.teamAgentCompanies.data.length === 1) {
        this.selectTeamAgentCompany(this.teamAgentCompanies.data[0]);
      }
    }).catch(() => {
      this.teamAgentCompanies.data = [];
      this.snackbar.openFromComponent(SnackBarComponent, {data: 'Unable to load team-agent companies'});
    }).finally(() => {
      this.teamAgentCompaniesLoading = false;
    });
  }

  selectTeamAgentCompany(company: CompanyInfo): void {
    this.selectedTeamAgentCompany = company;
    this.loadTeamsForTeamAgentCompany(company);
  }

  createTeamAgentTeam(): void {
    const company = this.selectedTeamAgentCompany;
    if (!company?.id) {
      return;
    }

    this.uiAlertService.presentFieldInput('Create New Team', 'Team Name', '').then(teamName => {
      const normalizedTeamName = `${teamName || ''}`.trim();
      if (!normalizedTeamName) {
        return;
      }

      this.teamAgentTeamsLoading = true;
      this.teamsService.createCompanyScopedPayrollTeam(company.id, {
        name: normalizedTeamName,
      }).then(() => {
        this.snackbar.openFromComponent(SnackBarComponent, {data: `${normalizedTeamName} created`});
        this.loadTeamsForTeamAgentCompany(company);
      }).catch(() => {
        this.teamAgentTeamsLoading = false;
        this.snackbar.openFromComponent(SnackBarComponent, {data: `Unable to create ${normalizedTeamName}`});
      });
    });
  }

  private loadTeamsForTeamAgentCompany(company: CompanyInfo): void {
    if (!company?.id) {
      return;
    }

    this.teamAgentTeams.data = [];
    this.teamAgentTeamsLoading = true;
    this.teamsService.getTeamsForCompany(company.id).then(teams => {
      const nextTeams = teams?.plain?.() || teams || [];
      this.teamAgentTeams.data = Array.isArray(nextTeams)
        ? nextTeams.sort((left: any, right: any) => `${left.name || ''}`.localeCompare(`${right.name || ''}`))
        : [];
    }).catch(() => {
      this.teamAgentTeams.data = [];
      this.snackbar.openFromComponent(SnackBarComponent, {data: `Unable to load teams for ${company.name}`});
    }).finally(() => {
      this.teamAgentTeamsLoading = false;
    });
  }

  loginAsTeamAgent(team: TeamAgentTeam): void {
    if (!team?.id || !this.selectedTeamAgentCompany?.id) {
      return;
    }

    this.sessionService.loginTeamAgentByTeamId(team.id, this.selectedTeamAgentCompany.id).catch(() => {
      this.snackbar.openFromComponent(SnackBarComponent, {data: `Unable to log in for ${team.name}`});
    });
  }

  private loadPartners(): void {
    this.partnersLoadPromise = this.partnersService.getAllPartners().then(partners => {
      this.partners = partners || [];
      this.partnerLookup = this.buildPartnerLookup(partners || []);
      if (this.companies.length > 0) {
        this.dataSource.data = this.transformToNested(this.companies);
      }
      return this.partners;
    }).catch(error => {
      console.error('Failed to load partners for company tree', error);
      this.partners = [];
      return this.partners;
    });
  }

  private async ensurePartnersLoaded(): Promise<void> {
    if (this.partners.length > 0) {
      return;
    }

    if (this.partnersLoadPromise) {
      await this.partnersLoadPromise.catch(() => []);
    }
  }

  public hasRoleAssignments(node: Node): boolean {
    return node?.type !== 'team' && (node?.roleAssignmentCount || 0) > 0;
  }

  private transformToNested(data: any[], parentId: string | null = null): any[] {
    return data.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    }).filter(item => parentId === null ? !item.parentId : item.parentId === parentId)
        .map(item => ({
          ...item,
          partnerDisplays: this.getPartnerDisplays(item),
          roleAssignmentCount: this.roleAssignmentDetails.get(item.id)?.length || 0,
          roleAssignmentTooltip: this.getRoleAssignmentTooltip(item.id),
          children: this.transformToNested(data, item.id)
        }));
  }

  private buildRoleAssignmentDetails(companyUsers: any[]): Map<string, string[]> {
    const details = new Map<string, string[]>();

    if (!Array.isArray(companyUsers)) {
      return details;
    }

    companyUsers.forEach(companyUser => {
      const companyId = `${companyUser?.companyId || ''}`.trim();
      if (!companyId) {
        return;
      }

      details.set(companyId, [
        ...(details.get(companyId) || []),
        this.getRoleAssignmentDisplay(companyUser),
      ]);
    });

    details.forEach((values, companyId) => {
      details.set(companyId, values.sort((left, right) => left.localeCompare(right)));
    });

    return details;
  }

  private getRoleAssignmentDisplay(companyUser: any): string {
    const name = `${companyUser?.name || ''}`.trim();
    const email = `${companyUser?.email || ''}`.trim();
    const role = `${companyUser?.role || ''}`.trim();
    const holder = [name, email].filter(value => !!value).join(' - ');
    return holder && role ? `${holder} (${role})` : holder;
  }

  private getRoleAssignmentTooltip(companyId: string): string {
    return (this.roleAssignmentDetails.get(companyId) || [])
      .filter(value => !!value)
      .join('\n');
  }

  private buildPartnerLookup(partners: Partner[]): Map<string, PartnerTreeDisplay> {
    const lookup = new Map<string, PartnerTreeDisplay>();

    partners.forEach((partner) => {
      const display: PartnerTreeDisplay = {
        id: String(partner.partnerId || partner.shortName || partner.name || ''),
        name: partner.name || partner.shortName || partner.partnerId || 'Unknown partner',
        iconUrl: partner.logoUrl || partner.lightLogoUrl || partner.horizontalLogoUrl || undefined,
      };

      this.getPartnerLookupKeys(partner).forEach((key) => {
        lookup.set(key, display);
      });
    });

    return lookup;
  }

  private getPartnerDisplays(item: any): PartnerTreeDisplay[] {
    const metadata = this.toObject(item?.metadata);
    const payrollConfig = this.toObject(item?.payrollConfig);
    const metadataPartners = Array.isArray(metadata?.partners) ? metadata.partners : [];
    const payrollConfigPartners = Array.isArray(payrollConfig?.partners) ? payrollConfig.partners : [];
    const partners = metadataPartners.length > 0 ? metadataPartners : payrollConfigPartners;
    const seenIds = new Set<string>();

    return partners
      .map((partner: any) => this.resolvePartnerDisplay(partner))
      .filter((partner: PartnerTreeDisplay | null): partner is PartnerTreeDisplay => {
        if (!partner || seenIds.has(partner.id)) {
          return false;
        }
        seenIds.add(partner.id);
        return true;
      });
  }

  private resolvePartnerDisplay(partner: any): PartnerTreeDisplay | null {
    const matchingPartner = this.getPartnerLookupKeys(partner)
      .map((key) => this.partnerLookup.get(key))
      .find((entry) => !!entry);

    if (matchingPartner) {
      return matchingPartner;
    }

    if (partner && typeof partner === 'object') {
      const fallbackName = partner.name || partner.partnerName || partner.shortName || partner.partnerId || partner.id;

      if (!fallbackName) {
        return null;
      }

      return {
        id: String(partner.partnerId || partner.id || partner.shortName || fallbackName),
        name: String(fallbackName),
        iconUrl: partner.logoUrl || partner.iconUrl || partner.avatarUrl || undefined,
      };
    }

    if (partner === undefined || partner === null || partner === '') {
      return null;
    }

    return {
      id: String(partner),
      name: String(partner),
    };
  }

  private getPartnerLookupKeys(partner: any): string[] {
    if (!partner) {
      return [];
    }

    const values = partner && typeof partner === 'object'
      ? [partner.partnerId, partner.id, partner.shortName, partner.name, partner.partnerName]
      : [partner];

    return values
      .filter((value) => value !== undefined && value !== null && value !== '')
      .reduce((keys: string[], value) => {
        const normalized = String(value).trim().toLowerCase();

        if (!normalized) {
          return keys;
        }

        keys.push(normalized);
        if (normalized.includes('.')) {
          keys.push(normalized.split('.')[0]);
        }
        return keys;
      }, []);
  }

  private getCompanySnackRewardBrand(company: any, node?: Node): string {
    let currentCompany = company || node;

    while (currentCompany) {
      const brand = this.getDirectCompanySnackRewardBrand(currentCompany);
      if (brand) {
        return brand;
      }

      currentCompany = currentCompany?.parentId
        ? this.companies.find(candidate => candidate.id === currentCompany.parentId)
        : undefined;
    }

    return this.getDirectCompanySnackRewardBrand(node);
  }

  private buildAvailableProductBrandOptions(): CompanyProductsBrandOption[] {
    const seenIds = new Set<string>();

    return (this.partners || [])
      .reduce((options: CompanyProductsBrandOption[], partner) => {
        const slug = this.normalizeBrandValue(partner.redirectUrl || partner.shortName || partner.name);
        const id = `${partner.partnerId || slug || partner.shortName || partner.name || ''}`.trim();
        if (!id || seenIds.has(id)) {
          return options;
        }
        seenIds.add(id);

        options.push({
          id,
          label: partner.name || partner.shortName || id,
          slug,
          subtitle: slug && slug !== `${partner.name || ''}`.trim().toLowerCase() ? slug : '',
        });

        return options;
      }, []);
  }

  private buildAvailableProductCompanyOptions(currentCompanyId: string): CompanyProductsCompanyOption[] {
    return (this.companies || [])
      .filter((company) => company.id !== currentCompanyId && company.type !== 'team')
      .map((company) => {
        const parent = company.parentId
          ? this.companies.find((candidate) => candidate.id === company.parentId)
          : undefined;
        const context = [company.type, parent?.name].filter((value) => !!value).join(' - ');
        return {
          id: company.id,
          label: company.name || company.id,
          subtitle: context,
        };
      })
      .sort((left, right) => left.label.localeCompare(right.label) || left.id.localeCompare(right.id));
  }

  private buildSnackRewardCompanyOptions(currentCompanyId: string): CompanySnackRewardsCompanyOption[] {
    return this.buildAvailableProductCompanyOptions(currentCompanyId).map((option) => {
      const company = (this.companies || []).find((candidate) => candidate.id === option.id);
      return {
        ...option,
        brand: this.getCompanySnackRewardBrand(company),
      };
    });
  }

  private resolveAvailableProductInitialBrandId(company: any, node?: Node): string {
    const brand = this.getCompanySnackRewardBrand(company, node);
    if (!brand) {
      return '';
    }

    const normalizedBrand = this.normalizeBrandValue(brand);
    const matchingPartner = (this.partners || []).find((partner) => this.getPartnerLookupKeys(partner).includes(normalizedBrand));
    return `${matchingPartner?.partnerId || normalizedBrand}`.trim();
  }

  private getDirectCompanySnackRewardBrand(company: any): string {
    if (!company) {
      return '';
    }

    const metadata = this.toObject(company?.metadata);
    const payrollConfig = this.toObject(company?.payrollConfig);
    const resolvedPayrollConfig = this.toObject(company?.resolvedPayrollConfig);
    const partners = [
      ...(Array.isArray(resolvedPayrollConfig?.partners) ? resolvedPayrollConfig.partners : []),
      ...(Array.isArray(payrollConfig?.partners) ? payrollConfig.partners : []),
      ...(Array.isArray(metadata?.partners) ? metadata.partners : []),
      ...(Array.isArray(company?.partnerDisplays) ? company.partnerDisplays : []),
      company?.brand,
      metadata?.brand,
      payrollConfig?.brand,
      resolvedPayrollConfig?.brand,
    ];

    return partners
      .map(partner => this.normalizePartnerBrand(partner))
      .find(brand => !!brand) || '';
  }

  private normalizePartnerBrand(partner: any): string {
    const display = this.resolvePartnerDisplay(partner);
    const values = partner && typeof partner === 'object'
      ? [
        partner.shortName,
        partner.brand,
        partner.slug,
        display?.id,
        display?.name,
        partner.name,
        partner.partnerName,
        partner.id,
        partner.partnerId,
      ]
      : [
        display?.id,
        display?.name,
        partner,
      ];
    const normalizedValues = values
      .map(value => this.normalizeBrandValue(value))
      .filter(value => !!value);

    return normalizedValues.find(value => !this.isUuidLike(value)) || '';
  }

  private normalizeBrandValue(value: any): string {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (!normalized) {
      return '';
    }

    const shortName = normalized.includes('.') ? normalized.split('.')[0] : normalized;
    return shortName.replace(/[^a-z0-9_-]/g, '');
  }

  private isUuidLike(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  private toObject(value: any): any {
    if (!value) {
      return {};
    }

    if (typeof value === 'object') {
      return value;
    }

    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }

    return {};
  }

  hasChild = (_: number, node: any) => !!node.children && node.children.length > 0;

  clearFilter(): void {
    this.filter = '';
    this.refreshCompanyTree();
  }

  private includeAncestorCompanies(companies: CompanyInfo[]): CompanyInfo[] {
    const companyById = new Map(this.companies.map(company => [company.id, company] as const));
    const visibleCompanies = new Map<string, CompanyInfo>();

    companies.forEach(company => {
      let currentCompany: CompanyInfo | undefined = company;

      while (currentCompany) {
        if (visibleCompanies.has(currentCompany.id)) {
          break;
        }
        visibleCompanies.set(currentCompany.id, currentCompany);
        currentCompany = currentCompany.parentId ? companyById.get(currentCompany.parentId) : undefined;
      }
    });

    return Array.from(visibleCompanies.values());
  }

  private expandTree(nodes: Node[]): void {
    nodes.forEach(node => {
      this.treeControl.expand(node);
      if (node.children?.length) {
        this.expandTree(node.children);
      }
    });
  }

  applyFilter(event: any): void {
    const rawValue = event.target.value || '';
    this.filter = rawValue;
    this.refreshCompanyTree();
  }

  get canUndoTreeMoveSeries(): boolean {
    return this.lastTreeMoveSeries.length > 0 && !this.isTreeMoveBusy;
  }

  get hasTreeMoveSeries(): boolean {
    return this.lastTreeMoveSeries.length > 0;
  }

  canDropTreeNode = (drag: any, drop: any): boolean => {
    return this.canMoveTreeNode(drag?.data, drop?.data);
  };

  dropTreeNode(event: CdkDragDrop<Node>): void {
    const draggedNode = event.item.data as Node;
    const newParentNode = event.container.data as Node;

    if (!this.canMoveTreeNode(draggedNode, newParentNode)) {
      return;
    }

    const previousParentId = this.getTreeNodeParentId(draggedNode);
    const action: TreeMoveAction = {
      id: draggedNode.id,
      name: draggedNode.name,
      type: draggedNode.type,
      previousParentId,
      nextParentId: newParentNode.id,
    };

    this.isTreeMoveBusy = true;
    this.persistTreeMove(action).then(() => {
      this.lastTreeMoveSeries.push(action);
      this.applyTreeMoveLocally(action.id, action.type, action.nextParentId, [action.nextParentId]);
      this.snackbar.openFromComponent(SnackBarComponent, {data: `${action.name} moved to ${newParentNode.name}`});
    }).catch(() => {
      this.snackbar.openFromComponent(SnackBarComponent, {data: `Unable to move ${action.name}`});
    }).finally(() => {
      this.isTreeMoveBusy = false;
    });
  }

  undoLastTreeMoveSeries(): void {
    if (!this.canUndoTreeMoveSeries) {
      return;
    }

    const actionsToUndo = [...this.lastTreeMoveSeries].reverse();
    const remainingActions = [...this.lastTreeMoveSeries];

    this.isTreeMoveBusy = true;
    this.undoTreeMoveActions(actionsToUndo, remainingActions).then(() => {
      this.lastTreeMoveSeries = [];
      this.snackbar.openFromComponent(SnackBarComponent, {data: `Company tree moves undone`});
    }).catch(() => {
      this.lastTreeMoveSeries = remainingActions;
      this.snackbar.openFromComponent(SnackBarComponent, {data: `Unable to undo all company tree moves`});
    }).finally(() => {
      this.isTreeMoveBusy = false;
    });
  }

  private async undoTreeMoveActions(actionsToUndo: TreeMoveAction[], remainingActions: TreeMoveAction[]): Promise<void> {
    for (const action of actionsToUndo) {
      const undoAction: TreeMoveAction = {
        ...action,
        nextParentId: action.previousParentId,
      };

      await this.persistTreeMove(undoAction);
      remainingActions.pop();
      this.applyTreeMoveLocally(action.id, action.type, action.previousParentId, [action.previousParentId]);
    }
  }

  private persistTreeMove(action: TreeMoveAction): Promise<any> {
    if (action.type === 'team') {
      return this.teamsService.modifyTeam(action.id, {companyId: action.nextParentId});
    }

    return this.teamCompaniesService.modifyCompany(action.id, {parentId: action.nextParentId});
  }

  private canMoveTreeNode(draggedNode?: Node, newParentNode?: Node): boolean {
    if (this.isTreeMoveBusy || !draggedNode?.id || !newParentNode?.id) {
      return false;
    }

    if (newParentNode.type === 'team' || draggedNode.id === newParentNode.id) {
      return false;
    }

    if (this.getTreeNodeParentId(draggedNode) === newParentNode.id) {
      return false;
    }

    if (draggedNode.type === 'team') {
      return newParentNode.type === 'employer';
    }

    return !this.createsCircularCompanyParent(draggedNode.id, newParentNode.id);
  }

  private createsCircularCompanyParent(companyId: string, newParentId: string): boolean {
    let currentCompany = this.companies.find(company => company.id === newParentId);
    const visitedCompanyIds = new Set<string>([companyId]);

    while (currentCompany) {
      if (visitedCompanyIds.has(currentCompany.id)) {
        return true;
      }

      visitedCompanyIds.add(currentCompany.id);
      currentCompany = currentCompany.parentId
        ? this.companies.find(company => company.id === currentCompany?.parentId)
        : undefined;
    }

    return false;
  }

  private getTreeNodeParentId(node: any): string {
    return `${node?.parentId || node?.companyId || ''}`;
  }

  private applyTreeMoveLocally(id: string, type: string, parentId: string, expandNodeIds: string[] = []): void {
    const matchingItem = this.companies.find(company => company.id === id) as any;

    if (!matchingItem) {
      return;
    }

    matchingItem.parentId = parentId;
    if (type === 'team') {
      matchingItem.companyId = parentId;
    }

    this.refreshCompanyTree(expandNodeIds);
  }

  private refreshCompanyTree(expandNodeIds: string[] = []): void {
    const value = this.filter.trim().toLowerCase();

    if (!value) {
      const expandedNodeIds = this.getExpandedNodeIds(this.dataSource.data);
      expandNodeIds.filter(id => !!id).forEach(id => expandedNodeIds.add(id));
      this.dataSource.data = this.transformToNested(this.companies);
      this.restoreExpandedNodes(this.dataSource.data, expandedNodeIds);
      return;
    }

    const matchingCompanies = this.companies.filter(company => company.name.toLowerCase().includes(value));
    const filteredCompanies = this.includeAncestorCompanies(matchingCompanies);
    this.dataSource.data = this.transformToNested(filteredCompanies);
    this.expandTree(this.dataSource.data);
  }

  private getExpandedNodeIds(nodes: Node[]): Set<string> {
    const expandedNodeIds = new Set<string>();

    nodes.forEach(node => {
      if (this.treeControl.isExpanded(node)) {
        expandedNodeIds.add(node.id);
      }

      if (node.children?.length) {
        this.getExpandedNodeIds(node.children).forEach(id => expandedNodeIds.add(id));
      }
    });

    return expandedNodeIds;
  }

  private restoreExpandedNodes(nodes: Node[], expandedNodeIds: Set<string>): void {
    nodes.forEach(node => {
      if (expandedNodeIds.has(node.id)) {
        this.treeControl.expand(node);
      }

      if (node.children?.length) {
        this.restoreExpandedNodes(node.children, expandedNodeIds);
      }
    });
  }

  addCompany(item?: Node): void {
    const dialogRef = this.dialog.open(EditCompanyDialogComponent, {
      data: new EditCompanyDialogModel({}, undefined)
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      this.teamCompaniesService.createCompany(dialogResult.name, dialogResult.payrollConfig, dialogResult.domainName, dialogResult.type, item?.id || this.dataSource.data[0].id,
          dialogResult.EIN, dialogResult.SIC, dialogResult.address1, dialogResult.address2, dialogResult.city, dialogResult.state, dialogResult.postcode).then (company => {
        this.teamCompaniesService.uploadLogo(company.id, dialogResult.imageData).then(result => {
          this.teamCompaniesService.modifyCompany(company.id, {logoUrl: result.url}).then(() => {
            this.snackbar.openFromComponent(SnackBarComponent, {data: `New company added`});
          });
          this.loadItems();
        });
      });
    });
  }

  editCompany(company: CompanyInfo): void {
    const dialogRef = this.dialog.open(EditCompanyDialogComponent, {
      data: new EditCompanyDialogModel(company, undefined)
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      this.teamCompaniesService.uploadLogo(company.id, dialogResult.imageData).then(result => {
        dialogResult.logoUrl = result.url;
        delete dialogResult.imageData;
        this.teamCompaniesService.modifyCompany(company.id, dialogResult).then(() => {
          this.snackbar.openFromComponent(SnackBarComponent, {data: `Company updated`});
          this.loadItems();
        });
      });
    });
  }

  setManagers(company: CompanyInfo): void {
    const dialogRef = this.dialog.open(EditCompanyManagersDialogComponent, {
      data: new EditCompanyManagersDialogModel(company)
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
        this.loadItems();
    });
  }

  removeCompany(company: CompanyInfo): void {
    this.teamsService.getAllTeams().then(teams => {
      if (teams.find((team: { companyId: string; }) => team.companyId === company.id)) {
        this.snackbar.openFromComponent(SnackBarComponent, {data:
              `This company is associated with the following teams and cannot be deleted until the teams are no longer associated with it: ${teams.filter((team: { companyId: string; }) => team.companyId === company.id).map((team: { name: any; }) => team.name).join(', ')}`});
      } else {
        this.uiAlertService.presentAlertConfirm(`Do you really want to remove this company?`).then(confirm => {
          if (confirm) {
            this.teamCompaniesService.deleteCompany(company.id).then (_ => {
              this.snackbar.openFromComponent(SnackBarComponent, {data: `Company deleted`});
              this.loadItems();
            });
          }
        });
      }
    });
  }

  showJSON(value: any): string {
    return JSON.stringify(value);
  }

  parentName(company: CompanyInfo): string | undefined {
    return this.companies.find(thisCompany => thisCompany.id === company.parentId)?.name;
  }

  setCompanyParent(company: CompanyInfo): void {
    const dialogRef = this.dialog.open(SetTeamCompanyDialogComponent, {
      data: new SetTeamCompanyComponentModel('', 'Set Parent Company',
          '', 'Set Parent Company', 'Remove Parent Company', false)
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult !== undefined) {
        if (dialogResult) {
          const visitedCompanies: CompanyInfo[] = [];
          visitedCompanies.push(company);
          let thisCompany: CompanyInfo | undefined = this.companies.find(newParentCompany => newParentCompany.id === dialogResult);
          while (thisCompany) {
            // @ts-ignore
            if (visitedCompanies.find(visitedCompany => visitedCompany.id === thisCompany.id)) {
              this.snackbar.openFromComponent(SnackBarComponent, {data: `The parent company you have chosen creates a circular reference. Companies must be arranged as a tree with no loops.`});
              return;
            }
            visitedCompanies.push(thisCompany);
            // @ts-ignore
            thisCompany = this.companies.find(itemCompany => itemCompany.id === thisCompany.parentId);
          }
        }
        this.teamCompaniesService.modifyCompany(company.id, {parentId: dialogResult}).then(_ => {
          this.loadItems();
          this.snackbar.openFromComponent(SnackBarComponent, {data: dialogResult ? `Parent company set for ${company.name}` : `Parent company removed for ${company.name}`});
        });
      }
    });

  }

  setTeamEmployer(item: Node): void {
    const dialogRef = this.dialog.open(SetTeamCompanyDialogComponent, {
      data: new SetTeamCompanyComponentModel('', 'Set Team Employer',
          'You can override the default association of teams to employers based on the team admin\'s email domain name.', 'Set Team Employer', 'Reset to Default', true)
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult !== undefined) {
        this.teamsService.modifyTeam(item.id, {companyId: dialogResult}).then(_ => {
          this.snackbar.openFromComponent(SnackBarComponent, {data: `Employer set for ${item.name}`});
        });
      }
    });
  }

  viewTeam(item: Node): void {
    this.router.navigate(['managewellspace/overview', item.id]);
  }

  getNodeRoute(item: Node): string[] | undefined {
    switch (item.type) {
      case 'broker':
        return ['/brokeragency/broker/broker-agencies', item.id];
      case 'agency':
        return ['/brokeragency/agency/agency-clients', item.id];
      case 'employer':
        return ['/brokeragency/clients/client-census', item.id];
      case 'team':
        return ['/managewellspace/overview', item.id];
      default:
        return undefined;
    }
  }

  navigateToNode(item: Node, event?: Event): void {
    event?.stopPropagation();
    const route = this.getNodeRoute(item);

    if (!route) {
      return;
    }

    this.router.navigate(route);
  }

  export(): void {
    this.exportToCsvService.export('All Companies', this.dataSource.data);
  }

  logoError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.style.display = 'none';
  }

  setCompanyOffers(item: any): void {
    this.dialog.open(CompanyOfferSelectionComponent, { data: { id: item.id, name: item.name }, width: '600px', maxWidth: '94vw' });
  }

  setProducts(item: Node): void {
    this.teamCompaniesService.getCompany(item.id).then(async company => {
      await this.ensurePartnersLoaded();
      const rawPayrollConfig = this.teamCompaniesService.getRawPayrollConfig(company);
      const resolvedPayrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(company);
      const canInherit = !!company?.parentId;
      const parentCompanyName = this.parentName(company) || '';
      const parentCompany = canInherit ? await this.teamCompaniesService.getCompany(company.parentId) : undefined;
      const parentAvailableProductIds = parentCompany
        ? this.teamCompaniesService.getAvailableProductIds(parentCompany)
        : [];
      const parentAlwaysActiveProductIds = parentCompany
        ? this.teamCompaniesService.getAlwaysActiveProductIds(parentCompany)
        : [];
      const parentPayrollDeductionDisabledProductIds = parentCompany
        ? this.teamCompaniesService.getPayrollDeductionDisabledProductIds(parentCompany)
        : [];
      const parentAllowanceControlDisabledProductIds = parentCompany
        ? this.teamCompaniesService.getAllowanceControlDisabledProductIds(parentCompany)
        : [];
      const productPayrollBenefitCategoriesMap = resolvedPayrollConfig.productPayrollBenefitCategoriesMap || {};
      const payrollBenefitSubcategoryDefinitionsByCategory =
        resolvedPayrollConfig.payrollBenefitSubcategoryDefinitionsByCategory || {};
      const brandOptions = this.buildAvailableProductBrandOptions();
      const companyOptions = this.buildAvailableProductCompanyOptions(item.id);
      const initialBrandId = this.resolveAvailableProductInitialBrandId(company, item);
      if (initialBrandId && !brandOptions.some((brand) => brand.id === initialBrandId)) {
        brandOptions.unshift({
          id: initialBrandId,
          label: initialBrandId,
          slug: initialBrandId,
          subtitle: '',
        });
      }
      const dialogRef = this.dialog.open(CompanyProductsDialogComponent, {
        data: new CompanyProductsDialogModel(
          this.teamCompaniesService.getAvailableProductIds(company),
          '',
          item.name,
          canInherit,
          parentCompanyName,
          this.getFieldMode(rawPayrollConfig, 'products', canInherit),
          parentAvailableProductIds,
          this.teamCompaniesService.getAlwaysActiveProductIds({ resolvedPayrollConfig }),
          parentAlwaysActiveProductIds,
          productPayrollBenefitCategoriesMap,
          payrollBenefitSubcategoryDefinitionsByCategory,
          this.teamCompaniesService.getPayrollDeductionDisabledProductIds({ resolvedPayrollConfig }),
          parentPayrollDeductionDisabledProductIds,
          true,
          brandOptions,
          initialBrandId,
          companyOptions,
          this.teamCompaniesService.getAllowanceControlDisabledProductIds({ resolvedPayrollConfig }),
          parentAllowanceControlDisabledProductIds,
          true,
        )
      });
      dialogRef.afterClosed().subscribe(dialogResult => {
        if (dialogResult !== undefined) {
          const parsedResult = this.parseProductsDialogResult(dialogResult);
          const resolvedProductIds = parsedResult.mode === 'custom'
            ? [...parsedResult.products]
            : parsedResult.mode === 'inherit'
            ? [...parentAvailableProductIds]
            : [];
          const nextAlwaysActiveProducts = parsedResult.mode === 'none'
            ? []
            : parsedResult.alwaysActiveProducts.filter((productId) => resolvedProductIds.includes(productId));
          const nextRequiresApprovalProducts = parsedResult.mode === 'custom'
            ? parsedResult.requiresApprovalProducts.filter((productId) => resolvedProductIds.includes(productId))
            : [];
          const nextPayrollDeductionDisabledProducts = parsedResult.mode === 'custom'
            ? parsedResult.payrollDeductionDisabledProducts.filter((productId) => resolvedProductIds.includes(productId))
            : [];
          const nextAllowanceControlDisabledProducts = parsedResult.allowanceControlDisabledProducts === undefined
            ? undefined
            : parsedResult.mode === 'custom'
              ? parsedResult.allowanceControlDisabledProducts.filter((productId) => resolvedProductIds.includes(productId))
              : [];
          this.teamCompaniesService.modifyCompanyPayrollConfig(item.id, (latestPayrollConfig, latestCompany) => {
            const nextPayrollConfig = { ...latestPayrollConfig };
            this.applyFieldOverride(nextPayrollConfig, 'products', parsedResult.mode, [...parsedResult.products]);
            this.applyFieldOverride(
              nextPayrollConfig,
              'alwaysActiveProducts',
              parsedResult.mode,
              [...nextAlwaysActiveProducts]
            );
            this.applyFieldOverride(
              nextPayrollConfig,
              'payrollDeductionDisabledProducts',
              parsedResult.mode,
              [...nextPayrollDeductionDisabledProducts]
            );
            if (nextAllowanceControlDisabledProducts !== undefined) {
              this.applyFieldOverride(
                nextPayrollConfig,
                'allowanceControlDisabledProducts',
                parsedResult.mode,
                [...nextAllowanceControlDisabledProducts]
              );
            }
            if (parsedResult.mode !== 'inherit') {
              this.pruneProductPayrollDeductionMap(
                nextPayrollConfig,
                resolvedProductIds,
                nextPayrollDeductionDisabledProducts,
              );
            }
            const nextProductPayrollBenefitCategoriesMap = this.applyRequiresApprovalSelectionsToProductPayrollBenefitCategoriesMap(
              latestPayrollConfig,
              latestCompany,
              resolvedProductIds,
              nextRequiresApprovalProducts,
              parsedResult.productPayrollBenefitCategoriesMap,
            );
            if (nextProductPayrollBenefitCategoriesMap) {
              nextPayrollConfig.productPayrollBenefitCategoriesMap = nextProductPayrollBenefitCategoriesMap;
            } else {
              delete nextPayrollConfig.productPayrollBenefitCategoriesMap;
            }
            if (parsedResult.payrollBenefitSubcategoryDefinitionsByCategory !== undefined) {
              nextPayrollConfig.payrollBenefitSubcategoryDefinitionsByCategory = {
                ...parsedResult.payrollBenefitSubcategoryDefinitionsByCategory,
              };
            }
            return nextPayrollConfig;
          }).then(() => {
            this.snackbar.openFromComponent(SnackBarComponent, {data: `Products set for ${item.name}`});
          });
        }
      });
    });
  }

  setProductTilesCategories(item: Node): void {
    this.teamCompaniesService.getCompany(item.id).then(company => {
      const rawPayrollConfig = this.teamCompaniesService.getRawPayrollConfig(company);
      const resolvedPayrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(company);
      const products: string[] = this.teamCompaniesService.getAvailableProductIds(company);
      const productTilesCategoriesMap = resolvedPayrollConfig.productTilesCategoriesMap || {};
      const canInherit = !!company?.parentId;
      const parentCompanyName = this.parentName(company) || '';
      const dialogRef = this.dialog.open(CompanyProductTilesCategoriesDialogComponent, {
        data: new CompanyProductTilesCategoriesDialogModel(
          products,
          productTilesCategoriesMap,
          item.name,
          canInherit,
          parentCompanyName,
          this.getFieldMode(rawPayrollConfig, 'productTilesCategoriesMap', canInherit)
        )
      });
      dialogRef.afterClosed().subscribe(dialogResult => {
        if (dialogResult !== undefined) {
          const parsedResult = this.parseProductTilesCategoriesDialogResult(dialogResult);
          this.teamCompaniesService.modifyCompanyPayrollConfig(item.id, (latestPayrollConfig) => {
            const nextPayrollConfig = { ...latestPayrollConfig };
            this.applyFieldOverride(
              nextPayrollConfig,
              'productTilesCategoriesMap',
              parsedResult.mode,
              { ...parsedResult.productTilesCategoriesMap }
            );
            return nextPayrollConfig;
          }).then(() => {
            this.snackbar.openFromComponent(SnackBarComponent, {data: `Product Tiles Categories set for ${item.name}`});
          });
        }
      });
    });
  }

  setProductPayrollBenefitCategories(item: Node): void {
    this.teamCompaniesService.getCompany(item.id).then(company => {
      const rawPayrollConfig = this.teamCompaniesService.getRawPayrollConfig(company);
      const resolvedPayrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(company);
      const products: string[] = this.teamCompaniesService.getAvailableProductIds(company);
      const productPayrollBenefitCategoriesMap = resolvedPayrollConfig.productPayrollBenefitCategoriesMap || {};
      const payrollBenefitSubcategoryDefinitionsByCategory =
        resolvedPayrollConfig.payrollBenefitSubcategoryDefinitionsByCategory || {};
      const canInherit = !!company?.parentId;
      const parentCompanyName = this.parentName(company) || '';
      const dialogRef = this.dialog.open(CompanyProductPayrollBenefitCategoriesDialogComponent, {
        data: new CompanyProductPayrollBenefitCategoriesDialogModel(
          products,
          productPayrollBenefitCategoriesMap,
          payrollBenefitSubcategoryDefinitionsByCategory,
          item.name,
          canInherit,
          parentCompanyName,
          this.getPayrollBenefitCategoriesMode(rawPayrollConfig, canInherit)
        )
      });
      dialogRef.afterClosed().subscribe(dialogResult => {
        if (dialogResult !== undefined) {
          const parsedResult = this.parseProductPayrollBenefitCategoriesDialogResult(dialogResult);
          this.teamCompaniesService.modifyCompanyPayrollConfig(item.id, (latestPayrollConfig) => {
            const nextPayrollConfig = { ...latestPayrollConfig };
            this.applyProductPayrollBenefitCategoriesOverride(
              nextPayrollConfig,
              parsedResult.mode,
              parsedResult.productPayrollBenefitCategoriesMap,
              parsedResult.payrollBenefitSubcategoryDefinitionsByCategory,
            );
            return nextPayrollConfig;
          }).then(() => {
            this.snackbar.openFromComponent(SnackBarComponent, {data: `Product Payroll Benefit Categories set for ${item.name}`});
          });
        }
      });
    });
  }

  setProductPlanLevels(item: Node): void {
    this.payrollBenefitsAssignmentService.loadProductPlanSettingsForCompany(item.id).then(planLevelsLoad => {
      const company = planLevelsLoad.company;
      const rawPayrollConfig = this.teamCompaniesService.getRawPayrollConfig(company);
      const canInherit = !!company?.parentId;
      const parentCompanyName = this.parentName(company) || '';
      const dialogRef = this.dialog.open(CompanyProductPlanLevelsDialogComponent, {
        data: new CompanyProductPlanLevelsDialogModel(
          planLevelsLoad.benefitProducts,
          planLevelsLoad.baselineSelectedPlanSelectionsMap,
          item.name,
          canInherit,
          parentCompanyName,
          this.getProductPlanLevelsMode(rawPayrollConfig, canInherit)
        )
      });
      dialogRef.afterClosed().subscribe(dialogResult => {
        if (dialogResult !== undefined) {
          const parsedResult = this.parseProductPlanLevelsDialogResult(dialogResult);
          this.teamCompaniesService.modifyCompanyPayrollConfig(item.id, (latestPayrollConfig) => {
            const nextPayrollConfig = { ...latestPayrollConfig };
            this.applyProductPlanLevelsOverride(
              nextPayrollConfig,
              parsedResult.mode,
              parsedResult.productPlanIdsMap,
              parsedResult.productPlanLabelsMap
            );
            return nextPayrollConfig;
          }).then(() => {
            this.snackbar.openFromComponent(SnackBarComponent, {data: `Product Plan Levels set for ${item.name}`});
          });
        }
      });
    });
  }

  async setSnackRewards(item: Node): Promise<void> {
    if (!this.isCompanySnackRewardSelectionEnabled) {
      this.snackbar.openFromComponent(SnackBarComponent, {data: 'Snack Rewards settings are disabled.'});
      return;
    }

    const company = await this.teamCompaniesService.getCompany(item.id);
    await this.ensurePartnersLoaded();
    const brand = this.getCompanySnackRewardBrand(company, item);
    if (!brand) {
      this.snackbar.openFromComponent(SnackBarComponent, {data: `Unable to determine a snack reward brand for ${item.name}`});
      return;
    }

    const rewards = await this.rewardsAdminService.getGloballySelectableSnackRewardCandidates();
    const rawPayrollConfig = this.teamCompaniesService.getRawPayrollConfig(company);
    const resolvedPayrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(company);
    const canInherit = !!company?.parentId;
    const parentCompanyName = this.parentName(company) || '';
    const brandOptions = this.buildAvailableProductBrandOptions();
    const companyOptions = this.buildSnackRewardCompanyOptions(item.id);
    const initialBrandId = this.resolveAvailableProductInitialBrandId(company, item);
    if (initialBrandId && !brandOptions.some((brandOption) => brandOption.id === initialBrandId)) {
      brandOptions.unshift({
        id: initialBrandId,
        label: initialBrandId,
        slug: brand,
        subtitle: '',
      });
    }
    const initialMode = this.hasOwnField(rawPayrollConfig, 'snackRewardIds')
      ? this.getFieldMode(rawPayrollConfig, 'snackRewardIds', canInherit)
      : 'inherit';
    const dialogRef = this.dialog.open(CompanySnackRewardsDialogComponent, {
      data: new CompanySnackRewardsDialogModel(
        rewards,
        Array.isArray(resolvedPayrollConfig?.snackRewardIds) ? resolvedPayrollConfig.snackRewardIds : [],
        item.name,
        brand,
        canInherit,
        parentCompanyName,
        initialMode,
        brandOptions,
        companyOptions,
        initialBrandId,
      ),
      autoFocus: false,
      maxWidth: '92vw',
      width: '820px',
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult !== undefined) {
        const parsedResult = this.parseSnackRewardsDialogResult(dialogResult);
        this.teamCompaniesService.modifyCompanyPayrollConfig(item.id, (latestPayrollConfig) => {
          const nextPayrollConfig = { ...latestPayrollConfig };
          this.applyFieldOverride(
            nextPayrollConfig,
            'snackRewardIds',
            parsedResult.mode,
            [...parsedResult.snackRewardIds]
          );
          return nextPayrollConfig;
        }).then(() => {
          this.snackbar.openFromComponent(SnackBarComponent, {data: `Snack Rewards set for ${item.name}`});
        });
      }
    });
  }

  openGroupProductOnboardingStatuses(item: Node): void {
    if (!item?.id) {
      return;
    }

    const dialogRef = this.dialog.open<
      GroupProductOnboardingStatusDialogComponent,
      GroupProductOnboardingStatusDialogData,
      boolean
    >(GroupProductOnboardingStatusDialogComponent, {
      data: {
        companyId: item.id,
        companyName: item.name,
      },
      autoFocus: false,
      maxWidth: '96vw',
      panelClass: 'group-product-onboarding-status-dialog-panel',
    });

    dialogRef.afterClosed().subscribe((updated) => {
      if (updated) {
        this.loadItems();
      }
    });
  }

  hasPayrollTeamPlusDefault(item: Node): boolean {
    return this.isPayrollAdminCompany(item)
      && this.teamCompaniesService.getPayrollAdminTeamsConfig(item)?.defaultPlan === 'plus';
  }

  isPayrollAdminCompany(item: Node): boolean {
    return item?.type === 'employer'
      && this.teamCompaniesService.isPayrollAdminCompany(item);
  }

  getPayrollTeamPlusDefaultActionLabel(item: Node): string {
    return `${this.hasPayrollTeamPlusDefault(item) ? 'Disable' : 'Enable'} Plus by Default for New Payroll Admin-Created Teams`;
  }

  togglePayrollTeamPlusDefault(item: Node): void {
    if (item?.type !== 'employer') {
      return;
    }

    this.teamCompaniesService.getCompany(item.id).then(company => {
      const enablePlusPlan = this.teamCompaniesService.getPayrollAdminTeamsConfig(company)?.defaultPlan !== 'plus';
      this.teamCompaniesService.modifyCompanyPayrollConfig(
        item.id,
        (_latestPayrollConfig, latestCompany) => this.teamCompaniesService.buildPayrollAdminTeamsPayrollConfig(
          latestCompany,
          {
            defaultPlan: enablePlusPlan ? 'plus' : undefined,
          },
        ),
      ).then((updatedCompany) => {
        item.payrollConfig = updatedCompany?.payrollConfig;
        this.snackbar.openFromComponent(SnackBarComponent, {
          data: `${enablePlusPlan ? 'Enabled' : 'Disabled'} Plus by default for new Payroll Admin-created teams for ${item.name}`
        });
      });
    });
  }

  private async loadCompanySnackRewardSelectionFeatureFlag(): Promise<void> {
    try {
      this.isCompanySnackRewardSelectionEnabled = await this.managedFeatureFlagsService.isEnabled(
        COMPANY_SNACK_REWARD_SELECTION_FLAG,
        'team-admin',
        {
          userId: this.usersService.getCurrentUserId() || undefined,
        },
      );
    } catch (error) {
      console.error('Failed to evaluate company snack reward selection feature flag', error);
      this.isCompanySnackRewardSelectionEnabled = false;
    }
  }

  hasIncludedPlusPlan(item: Node): boolean {
    return item?.type === 'employer'
      && this.teamCompaniesService.getPayrollAdminTeamsConfig(item)?.plusPlanIncluded === true;
  }

  getIncludedPlusPlanActionLabel(item: Node): string {
    return `${this.hasIncludedPlusPlan(item) ? 'Disable' : 'Enable'} Included Plus Plan (No Subscription Fee)`;
  }

  toggleIncludedPlusPlan(item: Node): void {
    if (item?.type !== 'employer') {
      return;
    }

    this.teamCompaniesService.getCompany(item.id).then(company => {
      const enableIncludedPlusPlan =
        this.teamCompaniesService.getPayrollAdminTeamsConfig(company)?.plusPlanIncluded !== true;
      this.teamCompaniesService.modifyCompanyPayrollConfig(
        item.id,
        (_latestPayrollConfig, latestCompany) => this.teamCompaniesService.buildPayrollAdminTeamsPayrollConfig(
          latestCompany,
          {
            plusPlanIncluded: enableIncludedPlusPlan ? true : undefined,
          },
        ),
      ).then((updatedCompany) => {
        item.payrollConfig = updatedCompany?.payrollConfig;
        this.snackbar.openFromComponent(SnackBarComponent, {
          data: `${enableIncludedPlusPlan ? 'Enabled' : 'Disabled'} Included Plus plan for ${item.name}`
        });
      });
    });
  }

  private getFieldMode(rawPayrollConfig: any, field: string, canInherit: boolean): CompanyProductSettingMode {
    if (!this.hasOwnField(rawPayrollConfig, field)) {
      return canInherit ? 'inherit' : 'custom';
    }

    const value = rawPayrollConfig[field];
    if (Array.isArray(value) && value.length === 0) {
      return 'none';
    }

    if (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
      return 'none';
    }

    return 'custom';
  }

  private getPayrollBenefitCategoriesMode(rawPayrollConfig: any, canInherit: boolean): CompanyProductSettingMode {
    const hasMappings = this.hasOwnField(rawPayrollConfig, 'productPayrollBenefitCategoriesMap');
    const hasDefinitions = this.hasOwnField(rawPayrollConfig, 'payrollBenefitSubcategoryDefinitionsByCategory');
    if (!hasMappings && !hasDefinitions) {
      return canInherit ? 'inherit' : 'custom';
    }

    const mappings = rawPayrollConfig?.productPayrollBenefitCategoriesMap;
    const definitions = rawPayrollConfig?.payrollBenefitSubcategoryDefinitionsByCategory;
    const areMappingsEmpty = mappings && typeof mappings === 'object' && !Array.isArray(mappings)
      ? Object.keys(mappings).length === 0
      : !mappings;
    const areDefinitionsEmpty = definitions && typeof definitions === 'object' && !Array.isArray(definitions)
      ? Object.keys(definitions).length === 0
      : !definitions;

    return areMappingsEmpty && areDefinitionsEmpty ? 'none' : 'custom';
  }

  private getProductPlanLevelsMode(rawPayrollConfig: any, canInherit: boolean): CompanyProductSettingMode {
    const hasPlanIdsField = this.hasOwnField(rawPayrollConfig, 'productPlanIdsMap');
    const hasPlanLabelsField = this.hasOwnField(rawPayrollConfig, 'productPlanLabelsMap');
    if (!hasPlanIdsField && !hasPlanLabelsField) {
      return canInherit ? 'inherit' : 'custom';
    }

    const planIdsMap = rawPayrollConfig?.productPlanIdsMap;
    const planLabelsMap = rawPayrollConfig?.productPlanLabelsMap;
    const isPlanIdsEmpty = planIdsMap && typeof planIdsMap === 'object' && !Array.isArray(planIdsMap)
      ? Object.keys(planIdsMap).length === 0
      : !planIdsMap;
    const isPlanLabelsEmpty = planLabelsMap && typeof planLabelsMap === 'object' && !Array.isArray(planLabelsMap)
      ? Object.keys(planLabelsMap).length === 0
      : !planLabelsMap;

    return isPlanIdsEmpty && isPlanLabelsEmpty ? 'none' : 'custom';
  }

  private hasOwnField(value: any, field: string): boolean {
    return Object.prototype.hasOwnProperty.call(value || {}, field);
  }

  private applyFieldOverride(target: any, field: string, mode: CompanyProductSettingMode, value: any): void {
    if (mode === 'inherit') {
      delete target[field];
      return;
    }

    if (mode === 'none') {
      target[field] = Array.isArray(value) ? [] : {};
      return;
    }

    target[field] = value;
  }

  private applyProductPlanLevelsOverride(
    target: any,
    mode: CompanyProductSettingMode,
    productPlanIdsMap: Record<string, string[]>,
    productPlanLabelsMap: Record<string, string[]>,
  ): void {
    if (mode === 'inherit') {
      delete target.productPlanIdsMap;
      delete target.productPlanLabelsMap;
      return;
    }

    if (mode === 'none') {
      target.productPlanIdsMap = {};
      target.productPlanLabelsMap = {};
      return;
    }

    target.productPlanIdsMap = { ...(productPlanIdsMap || {}) };
    target.productPlanLabelsMap = { ...(productPlanLabelsMap || {}) };
  }

  private applyProductPayrollBenefitCategoriesOverride(
    target: any,
    mode: CompanyProductSettingMode,
    productPayrollBenefitCategoriesMap: Record<string, ProductBenefitMapping>,
    payrollBenefitSubcategoryDefinitionsByCategory: PayrollBenefitSubcategoryDefinitionsByCategory,
  ): void {
    if (mode === 'inherit') {
      delete target.productPayrollBenefitCategoriesMap;
      delete target.payrollBenefitSubcategoryDefinitionsByCategory;
      return;
    }

    if (mode === 'none') {
      target.productPayrollBenefitCategoriesMap = {};
      target.payrollBenefitSubcategoryDefinitionsByCategory = {};
      return;
    }

    target.productPayrollBenefitCategoriesMap = { ...(productPayrollBenefitCategoriesMap || {}) };
    target.payrollBenefitSubcategoryDefinitionsByCategory = {
      ...(payrollBenefitSubcategoryDefinitionsByCategory || {}),
    };
  }

  private parseProductsDialogResult(dialogResult: any): {
    mode: CompanyProductSettingMode;
    products: string[];
    alwaysActiveProducts: string[];
    requiresApprovalProducts: string[];
    productPayrollBenefitCategoriesMap?: Record<string, ProductBenefitMapping>;
    payrollBenefitSubcategoryDefinitionsByCategory?: PayrollBenefitSubcategoryDefinitionsByCategory;
    payrollDeductionDisabledProducts: string[];
    allowanceControlDisabledProducts?: string[];
  } {
    if (Array.isArray(dialogResult)) {
      return {
        mode: 'custom',
        products: dialogResult,
        alwaysActiveProducts: [],
        requiresApprovalProducts: [],
        productPayrollBenefitCategoriesMap: undefined,
        payrollBenefitSubcategoryDefinitionsByCategory: undefined,
        payrollDeductionDisabledProducts: [],
        allowanceControlDisabledProducts: undefined,
      };
    }

    return {
      mode: dialogResult?.mode || 'custom',
      products: Array.isArray(dialogResult?.products) ? dialogResult.products : [],
      alwaysActiveProducts: Array.isArray(dialogResult?.alwaysActiveProducts) ? dialogResult.alwaysActiveProducts : [],
      requiresApprovalProducts: Array.isArray(dialogResult?.requiresApprovalProducts)
        ? dialogResult.requiresApprovalProducts
        : [],
      productPayrollBenefitCategoriesMap: this.hasOwnField(dialogResult, 'productPayrollBenefitCategoriesMap')
        ? normalizeProductPayrollBenefitMap(dialogResult?.productPayrollBenefitCategoriesMap || {})
        : undefined,
      payrollBenefitSubcategoryDefinitionsByCategory: this.hasOwnField(
        dialogResult,
        'payrollBenefitSubcategoryDefinitionsByCategory'
      )
        ? normalizePayrollBenefitSubcategoryDefinitionsByCategory(
          dialogResult?.payrollBenefitSubcategoryDefinitionsByCategory || {}
        )
        : undefined,
      payrollDeductionDisabledProducts: Array.isArray(dialogResult?.payrollDeductionDisabledProducts)
        ? dialogResult.payrollDeductionDisabledProducts
        : [],
      allowanceControlDisabledProducts: this.hasOwnField(dialogResult, 'allowanceControlDisabledProducts')
        ? (Array.isArray(dialogResult?.allowanceControlDisabledProducts)
          ? dialogResult.allowanceControlDisabledProducts
          : [])
        : undefined,
    };
  }

  private pruneProductPayrollDeductionMap(
    target: any,
    selectedProductIds: string[],
    payrollDeductionDisabledProductIds: string[],
  ): void {
    if (!target?.productPayrollDeductionMap || typeof target.productPayrollDeductionMap !== 'object') {
      return;
    }

    const disabledProductIds = new Set(payrollDeductionDisabledProductIds || []);
    const allowedProductIds = new Set(
      (selectedProductIds || []).filter((productId) => !disabledProductIds.has(productId))
    );
    const nextMap = Object.entries(target.productPayrollDeductionMap)
      .reduce((acc: Record<string, any>, [productId, config]) => {
        if (allowedProductIds.has(productId)) {
          acc[productId] = config;
        }
        return acc;
      }, {});

    if (Object.keys(nextMap).length > 0) {
      target.productPayrollDeductionMap = nextMap;
    } else {
      delete target.productPayrollDeductionMap;
    }
  }

  private applyRequiresApprovalSelectionsToProductPayrollBenefitCategoriesMap(
    latestPayrollConfig: any,
    latestCompany: any,
    selectedProductIds: string[],
    requiresApprovalProductIds: string[],
    productPayrollBenefitCategoriesMap?: Record<string, ProductBenefitMapping>,
  ): Record<string, ProductBenefitMapping> | undefined {
    const hasRawField = this.hasOwnField(latestPayrollConfig, 'productPayrollBenefitCategoriesMap');
    const rawMap = normalizeProductPayrollBenefitMap(latestPayrollConfig?.productPayrollBenefitCategoriesMap || {});
    const resolvedMap = normalizeProductPayrollBenefitMap(
      this.teamCompaniesService.getResolvedPayrollConfig(latestCompany)?.productPayrollBenefitCategoriesMap || {}
    );
    const selectedProductIdSet = new Set(selectedProductIds || []);
    const dialogMap = productPayrollBenefitCategoriesMap
      ? Object.entries(normalizeProductPayrollBenefitMap(productPayrollBenefitCategoriesMap))
        .reduce((result: Record<string, ProductBenefitMapping>, [productId, mapping]) => {
          if (selectedProductIdSet.has(productId)) {
            result[productId] = mapping;
          }
          return result;
        }, {})
      : undefined;

    if (dialogMap) {
      const resolvedSelectedMap = Object.entries(resolvedMap)
        .reduce((result: Record<string, ProductBenefitMapping>, [productId, mapping]) => {
          if (selectedProductIdSet.has(productId)) {
            result[productId] = mapping;
          }
          return result;
        }, {});
      if (hasRawField || !this.areProductPayrollBenefitCategoryMapsEqual(dialogMap, resolvedSelectedMap)) {
        return dialogMap;
      }

      return undefined;
    }

    const nextMap: Record<string, ProductBenefitMapping> = {
      ...rawMap,
    };
    const requiresApprovalSet = new Set(requiresApprovalProductIds || []);

    (selectedProductIds || []).forEach((productId) => {
      const effectiveMapping = resolvedMap[productId];
      const rawMapping = rawMap[productId];
      const baseMapping = rawMapping || effectiveMapping;
      if (!baseMapping?.category) {
        return;
      }

      const desiredRequiresApproval = requiresApprovalSet.has(productId);
      const currentRawRequiresApproval = rawMapping?.requiresApproval === true;
      if (rawMapping && currentRawRequiresApproval === desiredRequiresApproval) {
        return;
      }

      const currentEffectiveRequiresApproval = effectiveMapping?.requiresApproval === true;
      if (!rawMapping && currentEffectiveRequiresApproval === desiredRequiresApproval) {
        return;
      }

      nextMap[productId] = {
        category: baseMapping.category,
        ...(baseMapping.subcategory ? { subcategory: baseMapping.subcategory } : {}),
        ...(desiredRequiresApproval ? { requiresApproval: true } : {}),
      };
    });

    if (!hasRawField && Object.keys(nextMap).length === 0) {
      return undefined;
    }

    return nextMap;
  }

  private areProductPayrollBenefitCategoryMapsEqual(
    leftMap: Record<string, ProductBenefitMapping>,
    rightMap: Record<string, ProductBenefitMapping>,
  ): boolean {
    const normalizeEntries = (map: Record<string, ProductBenefitMapping>) => Object.entries(
      normalizeProductPayrollBenefitMap(map || {})
    )
      .sort(([leftProductId], [rightProductId]) => leftProductId.localeCompare(rightProductId))
      .map(([productId, mapping]) => [
        productId,
        mapping.category,
        mapping.subcategory || '',
        mapping.requiresApproval === true,
      ]);

    return JSON.stringify(normalizeEntries(leftMap)) === JSON.stringify(normalizeEntries(rightMap));
  }

  private parseProductTilesCategoriesDialogResult(dialogResult: any): CompanyProductTilesCategoriesDialogResult {
    if (!dialogResult?.mode) {
      return {
        mode: 'custom',
        productTilesCategoriesMap: dialogResult || {},
      };
    }

    return dialogResult;
  }

  private parseProductPayrollBenefitCategoriesDialogResult(dialogResult: any): CompanyProductPayrollBenefitCategoriesDialogResult {
    if (!dialogResult?.mode) {
      return {
        mode: 'custom',
        productPayrollBenefitCategoriesMap: dialogResult || {},
        payrollBenefitSubcategoryDefinitionsByCategory: {},
      };
    }

    return {
      mode: dialogResult.mode || 'custom',
      productPayrollBenefitCategoriesMap: dialogResult.productPayrollBenefitCategoriesMap || {},
      payrollBenefitSubcategoryDefinitionsByCategory:
        dialogResult.payrollBenefitSubcategoryDefinitionsByCategory || {},
    };
  }

  private parseProductPlanLevelsDialogResult(dialogResult: any): CompanyProductPlanLevelsDialogResult {
    if (!dialogResult?.mode) {
      return {
        mode: 'custom',
        productPlanIdsMap: dialogResult?.productPlanIdsMap || {},
        productPlanLabelsMap: dialogResult?.productPlanLabelsMap || {},
      };
    }

    return {
      mode: dialogResult.mode || 'custom',
      productPlanIdsMap: dialogResult.productPlanIdsMap || {},
      productPlanLabelsMap: dialogResult.productPlanLabelsMap || {},
    };
  }

  private parseSnackRewardsDialogResult(dialogResult: any): CompanySnackRewardsDialogResult {
    if (!dialogResult?.mode) {
      return {
        mode: 'custom',
        snackRewardIds: Array.isArray(dialogResult) ? dialogResult : [],
      };
    }

    return {
      mode: dialogResult.mode || 'custom',
      snackRewardIds: Array.isArray(dialogResult.snackRewardIds) ? dialogResult.snackRewardIds : [],
    };
  }

}
